import { useState, useEffect } from 'react'
import { MainLayout } from '../../components/layout/MainLayout'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Modal } from '../../components/ui/Modal'
import { User, Mail, Shield, Lock, Save, Key } from 'lucide-react'
import { usuarioService } from '../../services/usuarioService'
import type { Usuario } from '../../services/usuarioService'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from '../../utils/toast'
import { useAuthStore } from '../../store/authStore'

const profileSchema = z.object({
  name: z.string().min(3, 'Nome deve ter no mínimo 3 caracteres'),
  email: z.string().email('E-mail inválido'),
})

const passwordSchema = z.object({
  currentPassword: z.string().min(1, 'Senha atual é obrigatória'),
  newPassword: z.string().min(6, 'Nova senha deve ter no mínimo 6 caracteres'),
  confirmPassword: z.string().min(1, 'Confirme a nova senha'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'As senhas não coincidem',
  path: ['confirmPassword'],
})

type ProfileFormData = z.infer<typeof profileSchema>
type PasswordFormData = z.infer<typeof passwordSchema>

export function PerfilPage() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [profile, setProfile] = useState<Usuario | null>(null)
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false)
  const { user, setUser } = useAuthStore()

  const { register, handleSubmit, reset, formState: { errors } } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
  })

  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    reset: resetPassword,
    formState: { errors: passwordErrors },
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
  })

  const loadProfile = async () => {
    setLoading(true)
    try {
      const data = await usuarioService.getProfile()
      setProfile(data)
      reset({
        name: data.name,
        email: data.email,
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao carregar perfil'
      toast.error(message)
      console.error('Erro ao carregar perfil:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadProfile()
  }, [])

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      admin: 'Administrador',
      vendedor: 'Vendedor',
      operador: 'Operador',
    }
    return labels[role] || role
  }

  const getRoleColor = (role: string) => {
    const colors: Record<string, string> = {
      admin: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400',
      vendedor: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400',
      operador: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400',
    }
    return colors[role] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400'
  }

  const onSubmitProfile = async (data: ProfileFormData) => {
    setSaving(true)
    try {
      const updated = await usuarioService.updateProfile(data)
      setProfile(updated)
      
      // Atualiza o nome no store de autenticação
      if (user) {
        setUser({ ...user, name: updated.name })
      }
      
      toast.success('Perfil atualizado com sucesso!')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao atualizar perfil'
      toast.error(message)
      console.error('Erro ao atualizar perfil:', error)
    } finally {
      setSaving(false)
    }
  }

  const onSubmitPassword = async (data: PasswordFormData) => {
    setSaving(true)
    try {
      console.log('Iniciando alteração de senha...')
      console.log('Formulário válido:', { currentPassword: '***', newPassword: '***', confirmPassword: '***' })
      
      await usuarioService.updatePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      })
      
      console.log('Senha alterada com sucesso!')
      toast.success('Senha alterada com sucesso!')
      setIsPasswordModalOpen(false)
      resetPassword()
    } catch (error) {
      console.error('Erro capturado no componente:', error)
      const message = error instanceof Error ? error.message : 'Erro ao alterar senha'
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }

  const openPasswordModal = () => {
    resetPassword()
    setIsPasswordModalOpen(true)
  }

  if (loading) {
    return (
      <MainLayout>
        <div className="flex h-96 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      </MainLayout>
    )
  }

  if (!profile) {
    return (
      <MainLayout>
        <div className="flex h-96 items-center justify-center">
          <p className="text-gray-600 dark:text-gray-400">Erro ao carregar perfil</p>
        </div>
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      <div className="max-w-3xl space-y-6 animate-fadeIn">
        <div className="flex items-center gap-3">
          <User className="h-8 w-8 text-blue-600 dark:text-blue-400" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Meu Perfil
          </h1>
        </div>

        {/* Informações de Função */}
        <Card>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Informações da Conta
              </h2>
              <span className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${getRoleColor(profile.role)}`}>
                <Shield className="mr-1.5 h-4 w-4" />
                {getRoleLabel(profile.role)}
              </span>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <User className="h-4 w-4" />
                  Nome
                </div>
                <p className="mt-1 font-medium text-gray-900 dark:text-white">
                  {profile.name}
                </p>
              </div>

              <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <Mail className="h-4 w-4" />
                  Email
                </div>
                <p className="mt-1 font-medium text-gray-900 dark:text-white">
                  {profile.email}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <div
                className={`h-2 w-2 rounded-full ${
                  profile.ativo
                    ? 'bg-green-500'
                    : 'bg-red-500'
                }`}
              />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Conta {profile.ativo ? 'ativa' : 'inativa'}
              </span>
            </div>
          </div>
        </Card>

        {/* Formulário de Edição */}
        <Card>
          <form onSubmit={handleSubmit(onSubmitProfile)} className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Editar Perfil
            </h2>

            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                Nome *
              </label>
              <Input
                {...register('name')}
                placeholder="Seu nome completo"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.name.message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                Email *
              </label>
              <Input
                {...register('email')}
                type="email"
                placeholder="seu@email.com"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                disabled={saving}
                className="flex-1"
              >
                {saving ? (
                  <>
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Salvar Alterações
                  </>
                )}
              </Button>
            </div>
          </form>
        </Card>

        {/* Alteração de Senha */}
        <Card>
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Segurança
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Mantenha sua conta segura alterando sua senha regularmente.
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={openPasswordModal}
            >
              <Key className="mr-2 h-4 w-4" />
              Alterar Senha
            </Button>
          </div>
        </Card>
      </div>

      {/* Modal de Alteração de Senha */}
      <Modal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        title="Alterar Senha"
      >
        <form onSubmit={handleSubmitPassword(onSubmitPassword)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              Senha Atual *
            </label>
            <Input
              {...registerPassword('currentPassword')}
              type="password"
              placeholder="Digite sua senha atual"
            />
            {passwordErrors.currentPassword && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {passwordErrors.currentPassword.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              Nova Senha *
            </label>
            <Input
              {...registerPassword('newPassword')}
              type="password"
              placeholder="Digite a nova senha (mín. 6 caracteres)"
            />
            {passwordErrors.newPassword && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {passwordErrors.newPassword.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              Confirmar Nova Senha *
            </label>
            <Input
              {...registerPassword('confirmPassword')}
              type="password"
              placeholder="Digite novamente a nova senha"
            />
            {passwordErrors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {passwordErrors.confirmPassword.message}
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsPasswordModalOpen(false)}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Alterando...
                </>
              ) : (
                <>
                  <Lock className="mr-2 h-4 w-4" />
                  Alterar Senha
                </>
              )}
            </Button>
          </div>
        </form>
      </Modal>
    </MainLayout>
  )
}
